import { useState, useEffect, useCallback, useRef } from 'react'
import { Image, Video, MessageSquare, FileText, ListChecks, Home, Plus, Trash2, Check, ArrowUp, ArrowDown } from 'lucide-react'
import { masterclassService } from '../../services/masterclassService'
import LoadingSpinner from '../../components/LoadingSpinner'

const SUBTABS = [
  { id: 'takeaways', label: 'Takeaways', icon: ListChecks },
  { id: 'gallery', label: 'Gallery', icon: Image },
  { id: 'videos', label: 'Videos', icon: Video },
  { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
  { id: 'resources', label: 'Resources', icon: FileText },
  { id: 'homepage', label: 'Homepage', icon: Home },
]

export default function MasterclassMediaManager() {
  const [subtab, setSubtab] = useState('takeaways')
  const [masterclasses, setMasterclasses] = useState([])
  const [speakers, setSpeakers] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [m, s] = await Promise.all([
        masterclassService.adminGetMasterclasses(),
        masterclassService.getSpeakers(),
      ])
      setMasterclasses(m.masterclasses || [])
      setSpeakers(s.speakers || [])
    } catch (err) { console.error(err) } finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const save = async (fn, body) => {
    setMsg('')
    try {
      await fn(body)
      setMsg('Saved successfully')
    } catch (err) {
      setMsg(err.message || 'Failed to save')
    }
  }

  if (loading) return <LoadingSpinner className="py-16" />

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="text-2xl sm:text-3xl text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}>
          Masterclass Media Manager
        </h1>
      </div>

      {msg && <div className={`text-sm ${msg.includes('success') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{msg}</div>}

      <div className="flex gap-2 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible">
        {SUBTABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSubtab(id)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              subtab === id
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Icon className="w-4 h-4 shrink-0" /> {label}
          </button>
        ))}
      </div>

      <div className="py-2">
        {subtab === 'takeaways' && <TakeawaysForm masterclasses={masterclasses} onSave={save} />}
        {subtab === 'gallery' && <GalleryForm masterclasses={masterclasses} speakers={speakers} onSave={save} />}
        {subtab === 'videos' && <VideosForm masterclasses={masterclasses} speakers={speakers} onSave={save} />}
        {subtab === 'testimonials' && <TestimonialsForm masterclasses={masterclasses} speakers={speakers} onSave={save} />}
        {subtab === 'resources' && <ResourcesForm masterclasses={masterclasses} onSave={save} />}
        {subtab === 'homepage' && <HomepageForm onSave={save} />}
      </div>
    </div>
  )
}

function TakeawaysForm({ masterclasses, onSave }) {
  const [selectedId, setSelectedId] = useState('');
  const [takeaways, setTakeaways] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [labelErrors, setLabelErrors] = useState({}); // {index: true}
  const rowRefs = useRef([]);
  const labelInputRefs = useRef([]);

  const load = async (id) => {
    setSelectedId(id);
    setLoading(true);
    setSavedAt(false);
    try {
      const data = await masterclassService.getMasterclassDetail(id);
      const initial = data.takeaways?.length ? data.takeaways : [];
      setTakeaways(initial);
      setSavedSnapshot(initial);
      setLabelErrors({});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validate = (list) => {
    const errors = {};
    list.forEach((t, i) => {
      if (!t.label || !t.label.trim()) errors[i] = true;
    });
    setLabelErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addTakeaway = () => {
    setTakeaways([...takeaways, { label: '', description: '' }]);
    setSavedAt(false);
    // Focus the new row's Label field and scroll it into view on next paint.
    const newIdx = takeaways.length;
    requestAnimationFrame(() => {
      const input = labelInputRefs.current[newIdx];
      if (input) input.focus();
      const row = rowRefs.current[newIdx];
      if (row && row.scrollIntoView) {
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  };

  const updateField = (i, field, value) => {
    const copy = [...takeaways];
    copy[i] = { ...copy[i], [field]: value };
    setTakeaways(copy);
    if (field === 'label' && value.trim() && labelErrors[i]) {
      const errs = { ...labelErrors };
      delete errs[i];
      setLabelErrors(errs);
    }
    setSavedAt(false);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const copy = [...takeaways];
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    setTakeaways(copy);
    setLabelErrors({});
    setSavedAt(false);
  };

  const moveDown = (idx) => {
    if (idx === takeaways.length - 1) return;
    const copy = [...takeaways];
    [copy[idx + 1], copy[idx]] = [copy[idx], copy[idx + 1]];
    setTakeaways(copy);
    setLabelErrors({});
    setSavedAt(false);
  };

  const deleteTakeaway = (idx) => {
    setTakeaways(takeaways.filter((_, i) => i !== idx));
    setLabelErrors({});
    setSavedAt(false);
  };

  const save = async () => {
    if (!validate(takeaways)) return;
    setSaving(true);
    try {
      const payload = {
        masterclass_id: selectedId,
        // server will apply default icon "check" if omitted
        takeaways: takeaways.map(t => ({ label: t.label.trim(), description: t.description || '' })),
      };
      await onSave(masterclassService.adminSaveTakeaways, payload);
      setSavedSnapshot(takeaways);
      setSavedAt(true);
    } finally {
      setSaving(false);
    }
  };

  // Dirty check: disable Save when there are no unsaved changes.
  const isDirty = (() => {
    if (takeaways.length !== savedSnapshot.length) return true;
    return takeaways.some((t, i) => {
      const s = savedSnapshot[i] || {};
      return (t.label || '') !== (s.label || '') || (t.description || '') !== (s.description || '');
    });
  })();

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Session Takeaways</h3>
          {selectedId && !loading && takeaways.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-300/80 px-2 py-0.5 border border-gray-200 dark:border-gray-700">
              {takeaways.length} {takeaways.length === 1 ? 'takeaway' : 'takeaways'}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Add the key things attendees should learn or take away from this session.
        </p>
      </div>

      <div className="relative max-w-sm">
        <select
          value={selectedId}
          onChange={e => load(e.target.value)}
          className="w-full appearance-none px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none"
        >
          <option value="">Select masterclass</option>
          {masterclasses.map(m => (
            <option key={m.id} value={m.slug || m.id}>
              {m.topic}
            </option>
          ))}
        </select>
      </div>

      {loading && <LoadingSpinner size="sm" inline />}

      {selectedId && !loading && (
        <div className="space-y-4">
          {takeaways.length === 0 ? (
            <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-5 py-8 text-center space-y-3">
              <p className="text-gray-900 dark:text-white text-base font-semibold">No takeaways yet</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                Add the key lessons or practical points attendees should leave with.
              </p>
              <button
                onClick={addTakeaway}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add First Takeaway
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {takeaways.map((t, i) => {
                  const hasError = !!labelErrors[i];
                  const isFirst = i === 0;
                  const isLast = i === takeaways.length - 1;
                  return (
                    <div
                      key={i}
                      ref={el => (rowRefs.current[i] = el)}
                      className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-4 sm:px-5"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="text-gray-500 dark:text-gray-400 text-sm font-semibold tabular-nums pt-2 sm:pt-3 sm:w-8 shrink-0">
                          {String(i + 1).padStart(2, '0')}
                        </div>

                        <div className="flex-1 space-y-3 min-w-0">
                          <div className="space-y-1.5">
                            <label className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                              Label
                            </label>
                            <input
                              ref={el => (labelInputRefs.current[i] = el)}
                              value={t.label}
                              onChange={e => updateField(i, 'label', e.target.value)}
                              placeholder="e.g. Mastering Natural Light"
                              className={`w-full px-3 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border-b focus:outline-none transition-colors ${
                                hasError
                                  ? 'border-red-400/80 focus:border-red-500 dark:focus:border-red-400'
                                  : 'border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400'
                              }`}
                            />
                            {hasError && (
                              <p className="text-xs text-red-600 dark:text-red-400">Label is required</p>
                            )}
                          </div>

                          <div className="space-y-1.5">
                            <label className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                              Description <span className="lowercase tracking-normal text-gray-400 dark:text-gray-500">(optional)</span>
                            </label>
                            <textarea
                              rows={2}
                              value={t.description}
                              onChange={e => updateField(i, 'description', e.target.value)}
                              placeholder="Short description of what attendees will learn"
                              className="w-full px-3 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none resize-y"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-1.5 pt-3 mt-1 border-t border-gray-100 dark:border-gray-800">
                        <button
                          type="button"
                          onClick={() => moveUp(i)}
                          disabled={isFirst}
                          title="Move up"
                          aria-label="Move up"
                          className="inline-flex items-center justify-center w-9 h-9 text-gray-400 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-primary-500/60 dark:hover:border-primary-400/60 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:hover:border-gray-700 disabled:hover:text-gray-400 dark:hover:text-gray-300"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveDown(i)}
                          disabled={isLast}
                          title="Move down"
                          aria-label="Move down"
                          className="inline-flex items-center justify-center w-9 h-9 text-gray-400 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-primary-500/60 dark:hover:border-primary-400/60 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:hover:border-gray-700 disabled:hover:text-gray-400 dark:hover:text-gray-300"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTakeaway(i)}
                          title="Delete"
                          aria-label="Delete takeaway"
                          className="inline-flex items-center justify-center w-9 h-9 text-gray-400 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-red-400/60 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  onClick={addTakeaway}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Takeaway
                </button>
                <button
                  onClick={save}
                  disabled={saving || !isDirty}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <><LoadingSpinner size="sm" inline /> Saving…</>
                  ) : savedAt && !isDirty ? (
                    <><Check className="w-4 h-4" /> Takeaways saved</>
                  ) : (
                    <><Check className="w-4 h-4" /> Save Takeaways</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function GalleryForm({ masterclasses, speakers, onSave }) {
  const [form, setForm] = useState({ masterclass_id: '', speaker_id: '', image_url: '', image_file: null, caption: '', category: '', is_featured: false })
  const [preview, setPreview] = useState('')
  const [saving, setSaving] = useState(false)

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null
    setForm(f => ({ ...f, image_file: file }))
    if (file) {
      setPreview(URL.createObjectURL(file))
    } else {
      setPreview('')
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      // T14.7b: If a file is attached, send as multipart FormData for R2 public upload.
      let payload = form
      if (form.image_file) {
        payload = new FormData()
        for (const [key, value] of Object.entries(form)) {
          if (key === 'image_file') {
            payload.append('image_file', value)
          } else if (value !== null && value !== undefined && value !== '') {
            payload.append(key, typeof value === 'boolean' ? String(value) : value)
          }
        }
      }
      await onSave(masterclassService.adminCreateGallery, payload)
      setForm({ masterclass_id: '', speaker_id: '', image_url: '', image_file: null, caption: '', category: '', is_featured: false })
      setPreview('')
    } finally {
      setSaving(false)
    }
  }

  const effectivePreview = preview || form.image_url

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Gallery Image</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={form.masterclass_id} onChange={e => setForm(f => ({ ...f, masterclass_id: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="">Masterclass</option>
          {masterclasses.map(m => <option key={m.id} value={m.id}>{m.topic}</option>)}
        </select>
        <select value={form.speaker_id} onChange={e => setForm(f => ({ ...f, speaker_id: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="">Speaker (optional)</option>
          {speakers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Upload Image (JPG/PNG/WebP, max 10MB)</label>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} className="w-full px-4 py-3 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700" />
          {form.image_file && <p className="text-primary-600/70 dark:text-primary-400/70 text-xs mt-1">Selected: {form.image_file.name} ({(form.image_file.size / 1024).toFixed(1)} KB)</p>}
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Or enter Image URL manually</label>
          <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="Image URL (ignored if file uploaded above)" className="w-full px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        </div>

        {effectivePreview && (
          <div className="sm:col-span-2 flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <img src={effectivePreview} alt="Preview" className="w-20 h-20 object-cover border border-gray-200 dark:border-gray-700" />
            <div className="text-xs text-gray-400 dark:text-gray-300/80">
              <p className="font-semibold text-gray-900 dark:text-white">Image Preview</p>
              <p className="truncate max-w-xs">{form.image_file ? form.image_file.name : form.image_url}</p>
            </div>
          </div>
        )}

        <input value={form.caption} onChange={e => setForm(f => ({ ...f, caption: e.target.value }))} placeholder="Caption" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Category" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <label className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
          <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="w-4 h-4" /> Featured
        </label>
      </div>
      <button onClick={save} disabled={saving || (!form.image_file && !form.image_url)} className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50">
        {saving ? <LoadingSpinner size="sm" inline /> : <Plus className="w-4 h-4" />} Add Image
      </button>
    </div>
  )
}

function VideosForm({ masterclasses, speakers, onSave }) {
  const [form, setForm] = useState({ masterclass_id: '', speaker_id: '', title: '', youtube_id: '', category: '', description: '', is_featured: false })

  const save = () => {
    onSave(masterclassService.adminCreateVideo, form)
    setForm({ masterclass_id: '', speaker_id: '', title: '', youtube_id: '', category: '', description: '', is_featured: false })
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Video</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={form.masterclass_id} onChange={e => setForm(f => ({ ...f, masterclass_id: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="">Masterclass</option>
          {masterclasses.map(m => <option key={m.id} value={m.id}>{m.topic}</option>)}
        </select>
        <select value={form.speaker_id} onChange={e => setForm(f => ({ ...f, speaker_id: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="">Speaker (optional)</option>
          {speakers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Title" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={form.youtube_id} onChange={e => setForm(f => ({ ...f, youtube_id: e.target.value }))} placeholder="YouTube Video ID" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="Category" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <label className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
          <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="w-4 h-4" /> Featured
        </label>
      </div>
      <button onClick={save} className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"><Plus className="w-4 h-4" /> Add Video</button>
    </div>
  )
}

function TestimonialsForm({ masterclasses, speakers, onSave }) {
  const [form, setForm] = useState({ masterclass_id: '', speaker_id: '', student_name: '', course: '', rating: 5, review: '', is_featured: true, is_approved: true })

  const save = () => {
    onSave(masterclassService.adminCreateTestimonial, form)
    setForm({ masterclass_id: '', speaker_id: '', student_name: '', course: '', rating: 5, review: '', is_featured: true, is_approved: true })
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Testimonial</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={form.masterclass_id} onChange={e => setForm(f => ({ ...f, masterclass_id: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="">Masterclass</option>
          {masterclasses.map(m => <option key={m.id} value={m.id}>{m.topic}</option>)}
        </select>
        <select value={form.speaker_id} onChange={e => setForm(f => ({ ...f, speaker_id: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="">Speaker (optional)</option>
          {speakers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input value={form.student_name} onChange={e => setForm(f => ({ ...f, student_name: e.target.value }))} placeholder="Student Name" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={form.course} onChange={e => setForm(f => ({ ...f, course: e.target.value }))} placeholder="Course" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input type="number" min={1} max={5} value={form.rating} onChange={e => setForm(f => ({ ...f, rating: parseInt(e.target.value) }))} placeholder="Rating 1-5" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <textarea rows={3} value={form.review} onChange={e => setForm(f => ({ ...f, review: e.target.value }))} placeholder="Review" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
      </div>
      <button onClick={save} className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"><Plus className="w-4 h-4" /> Add Testimonial</button>
    </div>
  )
}

function ResourcesForm({ masterclasses, onSave }) {
  const [form, setForm] = useState({ masterclass_id: '', title: '', resource_type: 'download', file_url: '', resource_file: null, description: '', is_public: true })
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      // T14.7b: If a resource file is attached, send as multipart FormData for R2 public upload.
      let payload = form
      if (form.resource_file) {
        payload = new FormData()
        for (const [key, value] of Object.entries(form)) {
          if (key === 'resource_file') {
            payload.append('resource_file', value)
          } else if (value !== null && value !== undefined && value !== '') {
            payload.append(key, typeof value === 'boolean' ? String(value) : value)
          }
        }
      }
      await onSave(masterclassService.adminCreateResource, payload)
      setForm({ masterclass_id: '', title: '', resource_type: 'download', file_url: '', resource_file: null, description: '', is_public: true })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Resource</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <select value={form.masterclass_id} onChange={e => setForm(f => ({ ...f, masterclass_id: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="">Masterclass *</option>
          {masterclasses.map(m => <option key={m.id} value={m.id}>{m.topic}</option>)}
        </select>
        <select value={form.resource_type} onChange={e => setForm(f => ({ ...f, resource_type: e.target.value }))} className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none">
          <option value="presentation">Presentation / Slide Deck</option>
          <option value="speaker_notes">Speaker Notes</option>
          <option value="reference">Reference Guide</option>
          <option value="reading">Reading Material</option>
          <option value="download">Downloadable Document (PDF)</option>
        </select>
        <div className="sm:col-span-2">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Resource Title *" className="w-full px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Upload PDF Document (max 50MB)</label>
          <input type="file" accept="application/pdf" onChange={e => setForm(f => ({ ...f, resource_file: e.target.files?.[0] || null }))} className="w-full px-4 py-3 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700" />
          {form.resource_file && (
            <p className="text-primary-600/70 dark:text-primary-400/70 text-xs mt-1">
              Selected: {form.resource_file.name} ({(form.resource_file.size / (1024 * 1024)).toFixed(2)} MB)
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Or enter File URL manually</label>
          <input value={form.file_url} onChange={e => setForm(f => ({ ...f, file_url: e.target.value }))} placeholder="File URL (ignored if PDF uploaded above)" className="w-full px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        </div>
        <div className="sm:col-span-2">
          <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="w-full px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 resize-none" />
        </div>
        <label className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
          <input type="checkbox" checked={form.is_public} onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))} className="w-4 h-4" /> Public (Available to All Attendees)
        </label>
      </div>
      <button onClick={save} disabled={saving || !form.masterclass_id || !form.title || (!form.resource_file && !form.file_url)} className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50">
        {saving ? <LoadingSpinner size="sm" inline /> : <Plus className="w-4 h-4" />} Add Resource
      </button>
    </div>
  )
}

function HomepageForm({ onSave }) {
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    cta_button_text: '',
    featured_video_id: '',
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    masterclassService.adminGetHomepageSettings().then(d => {
      const map = {}
      ;(d.settings || []).forEach(s => map[s.key] = s.value)
      setSettings(s => ({ ...s, ...map }))
    }).catch(console.error).finally(() => setLoading(false))
  }, [])

  const save = () => {
    const payload = Object.entries(settings).map(([key, value]) => ({ key, value, category: 'homepage' }))
    onSave(masterclassService.adminSaveHomepageSettings, { settings: payload })
  }

  if (loading) return <LoadingSpinner className="py-8" />

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Homepage Settings</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <input value={settings.hero_title} onChange={e => setSettings(s => ({ ...s, hero_title: e.target.value }))} placeholder="Hero Title" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={settings.hero_subtitle} onChange={e => setSettings(s => ({ ...s, hero_subtitle: e.target.value }))} placeholder="Hero Subtitle" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={settings.cta_button_text} onChange={e => setSettings(s => ({ ...s, cta_button_text: e.target.value }))} placeholder="CTA Button Text" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
        <input value={settings.featured_video_id} onChange={e => setSettings(s => ({ ...s, featured_video_id: e.target.value }))} placeholder="Featured YouTube ID" className="px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" />
      </div>
      <button onClick={save} className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"><Check className="w-4 h-4" /> Save</button>
    </div>
  )
}
